import { useState, useEffect } from "react";
import { message } from "antd";
import { supabase } from "../lib/supabase";
import { ContainerInfo, BookingInfo, Product } from "../types/container-unloading";

/** Row from container unloading fetch; includes `bookings.eta` for Sendcloud ETA guard (not on base `BookingInfo`). */
export type ContainerUnloadingBooking = BookingInfo & { eta?: string | null };

export const useContainerUnloading = (containerId: string | undefined) => {
  const [container, setContainer] = useState<ContainerInfo | null>(null);
  const [bookings, setBookings] = useState<ContainerUnloadingBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [startingUnload, setStartingUnload] = useState(false);

  const fetchContainerData = async () => {
    try {
      setLoading(true);

      // Fetch container info
      const { data: containerData, error: containerError } = await supabase
        .from("containers")
        .select(
          "id, reference, status, unloading_status, unloading_started_at, unloading_completed_at"
        )
        .eq("id", containerId)
        .single();

      if (containerError) throw containerError;
      setContainer(containerData);

      // Fetch bookings with their products and delivery info
      const { data: bookingsData, error: bookingsError } = await supabase
        .from("bookings")
        .select(
          `
          id,
          status,
          eta,
          company_id,
          unloading_status,
          total_actual_cartons,
          total_actual_pallets,
          unloading_notes,
          company_info:company(name),
          transport_type_info:booking_transport_types(abbreviation),
          booking_delivery_info:booking_delivery(
            id,
            street_name,
            house_number,
            city,
            postal_code,
            country,
            shipment_status
          ),
          sendcloud_shipments(id),
          booking_purchase_orders(id)
        `
        )
        .eq("container_id", containerId);

      if (bookingsError) throw bookingsError;

      // Transform bookings data with proper type casting
      const transformedBookings = await Promise.all(
        (bookingsData || []).map(async (bookingData: any) => {
          // Fetch products for this booking
          const { data: productsData, error: productsError } = await supabase
            .from("booking_product")
            .select(
              `
              id, 
              name, 
              ean, 
              cartons, 
              actual_cartons,
              pallets,
              cbm,
              gross_weight,
              pieces
            `
            )
            .eq("booking_id", bookingData.id);

          if (productsError) throw productsError;

          // Transform products
          const products: Product[] = (productsData || []).map(
            (product: any) => ({
              id: product.id,
              booking_id: bookingData.id,
              booking_reference: `EFF${bookingData.id}${
                bookingData.transport_type_info?.abbreviation || ""
              }`,
              company_name: bookingData.company_info?.name || "Unknown",
              name: product.name,
              ean: product.ean,
              estimated_cartons: product.cartons || 0,
              actual_cartons: product.actual_cartons,
              pallets: product.pallets || null,
              completed: product.actual_cartons !== null && product.actual_cartons === (product.cartons || 0),
              cbm: product.cbm,
              gross_weight: product.gross_weight,
              pieces: product.pieces,
              // Company product dimensions not available yet - to be implemented later
              company_product_dimensions: null,
            })
          );

          // Determine delivery info
          const deliveryInfo = Array.isArray(bookingData.booking_delivery_info)
            ? bookingData.booking_delivery_info[0]
            : bookingData.booking_delivery_info;

          const hasDeliveryAddress = deliveryInfo && deliveryInfo.street_name;

          // Determine booking status
          const allProductsCounted = products.every((p) => p.completed);

          let status: BookingInfo["unloading_status"] = "pending";
          if (bookingData.unloading_status) {
            status = bookingData.unloading_status;
          } else if (allProductsCounted && products.length > 0) {
            // All bookings go to delivery_ready when counting is complete
            // Storage bookings only become "completed" after purchase order is created
            status = "delivery_ready";
          } else if (products.some((p) => p.completed)) {
            status = "counting";
          }

          // Count shipments for this booking
          const shipmentCount = Array.isArray(bookingData.sendcloud_shipments)
            ? bookingData.sendcloud_shipments.length
            : 0;

          // Count purchase orders for this booking
          const purchaseOrderCount = Array.isArray(bookingData.booking_purchase_orders)
            ? bookingData.booking_purchase_orders.length
            : 0;

          return {
            id: bookingData.id,
            booking_reference: `EFF${bookingData.id}${
              bookingData.transport_type_info?.abbreviation || ""
            }`,
            company_name: bookingData.company_info?.name || "Unknown",
            company_id: bookingData.company_id,
            status: bookingData.status,
            eta: bookingData.eta ?? null,
            products,
            unloading_status: status,
            total_actual_cartons: bookingData.total_actual_cartons,
            total_actual_pallets: bookingData.total_actual_pallets,
            internal_pallet_label_note: bookingData.internal_pallet_label_note,
            has_delivery_address: Boolean(hasDeliveryAddress),
            delivery_type: hasDeliveryAddress
              ? ("delivery" as const)
              : ("storage" as const),
            shipment_count: shipmentCount,
            purchase_order_count: purchaseOrderCount,
            delivery_info: deliveryInfo
              ? {
                  id: deliveryInfo.id,
                  street_name: deliveryInfo.street_name,
                  house_number: deliveryInfo.house_number,
                  city: deliveryInfo.city,
                  postal_code: deliveryInfo.postal_code,
                  country: deliveryInfo.country,
                  shipment_status: deliveryInfo.shipment_status,
                }
              : undefined,
          };
        })
      );

      setBookings(transformedBookings);
    } catch (error) {
      console.error("Error fetching container data:", error);
      message.error("Failed to load container data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!containerId) return;
    fetchContainerData();
  }, [containerId]);

  const startUnloading = async () => {
    try {
      setStartingUnload(true);

      const { error: containerError } = await supabase
        .from("containers")
        .update({
          unloading_status: "in_progress",
          unloading_started_at: new Date().toISOString(),
        })
        .eq("id", containerId);

      if (containerError) throw containerError;

      // Update local container state instead of full refresh
      setContainer((prev) =>
        prev
          ? {
              ...prev,
              unloading_status: "in_progress",
              unloading_started_at: new Date().toISOString(),
            }
          : null
      );

      message.success("Container unloading started!");
    } catch (error) {
      console.error("Error starting unloading:", error);
      message.error("Failed to start unloading");
    } finally {
      setStartingUnload(false);
    }
  };

  const resetContainer = async () => {
    try {
      setUpdating("container-reset");

      // Reset container status
      const { error: containerError } = await supabase
        .from("containers")
        .update({
          unloading_status: null,
          unloading_started_at: null,
          unloading_completed_at: null,
        })
        .eq("id", containerId);

      if (containerError) throw containerError;

      // Reset all bookings in this container
      const { error: bookingError } = await supabase
        .from("bookings")
        .update({
          unloading_status: null,
          total_actual_cartons: null,
          total_actual_pallets: null,
          unloading_notes: null,
        })
        .eq("container_id", containerId);

      if (bookingError) throw bookingError;

      // Reset all product counts in this container
      const bookingIds = bookings.map((b) => b.id);
      if (bookingIds.length > 0) {
        const { error: productError } = await supabase
          .from("booking_product")
          .update({ actual_cartons: null })
          .in("booking_id", bookingIds);

        if (productError) throw productError;
      }

      // Update local state to reflect reset
      setContainer((prev) =>
        prev
          ? {
              ...prev,
              unloading_status: null,
              unloading_started_at: null,
              unloading_completed_at: null,
            }
          : null
      );

      setBookings((prevBookings) =>
        prevBookings.map((booking) => ({
          ...booking,
          unloading_status: "pending",
          total_actual_cartons: null,
          total_actual_pallets: null,
          internal_pallet_label_note: null,
          products: booking.products.map((product) => ({
            ...product,
            actual_cartons: null,
            completed: false,
          })),
        }))
      );

      message.success("Container completely reset!");
    } catch (error) {
      console.error("Error resetting container:", error);
      message.error("Failed to reset container");
    } finally {
      setUpdating(null);
    }
  };

  return {
    container,
    bookings,
    loading,
    updating,
    startingUnload,
    setContainer,
    setBookings,
    setUpdating,
    fetchContainerData,
    startUnloading,
    resetContainer,
  };
};
